import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import clsx from 'clsx'
import { useModal } from '@ebay/nice-modal-react'
import { Icon } from '@iconify/react'
import { CheckboxChangeEvent } from 'antd/lib/checkbox'
import { Tag, Avatar, Checkbox, useMessage } from '@pepper/ui'

import { useGetWorkflowOnOffState } from 'utils/hooks/workflows/useGetWorkflowToggledState'
import downloadItems from 'utils/api/downloadItems'
import AssigneeDropdown from 'components/workflows/AssigneeDropdown'
import StatusDropdown from 'components/workflows/StatusDropdown'

import QuickActions from '../QuickActions/QuickActions'
import DeleteItemModal from '../DeleteModal/DeleteItemModal'
import s from './DocRow.module.css'

type Props = {
  companyId: string
  item: Item
  selectedItems: Item[]
  recentItems?: string[]
  assigneeList?: TeamMembers[]
  workflowStatusList?: WorkflowStatus[]
  setItem: (val: Item) => void
  setSelectedItems: Dispatch<SetStateAction<Item[]>>
  setOpenDrawer: (val: boolean) => void
  refetchItems: () => void
}

const Row = ({
  item,
  selectedItems,
  companyId,
  recentItems,
  assigneeList,
  workflowStatusList,
  setItem,
  setSelectedItems,
  setOpenDrawer,
  refetchItems,
}: Props) => {
  const [recentHighlight, setRecentHighlight] = useState(
    recentItems?.includes(item.id)
  )
  const { data: toggleState } = useGetWorkflowOnOffState({ companyId })
  const { show: showDeleteModal } = useModal(DeleteItemModal)
  const { showError } = useMessage()

  const handleClick = () => {
    window.open(`/${companyId}/items/${item.id}`, '_blank')
  }

  const handleWorkflowLogClick = () => {
    setItem(item)
    setOpenDrawer(true)
  }

  const handleDownload = () => {
    try {
      downloadItems([item], companyId, 'doc')
    } catch (error) {
      showError(error.message)
    }
  }

  const handleDelete = () => {
    showDeleteModal({
      items: [item],
      onSuccess: () => {
        refetchItems()
        setSelectedItems(prevItems =>
          prevItems.filter(_item => _item.id !== item.id)
        )
      },
    })
  }

  const handleSelect = (event: CheckboxChangeEvent) => {
    event.stopPropagation()
    const { checked } = event.target
    if (checked) {
      setSelectedItems([...selectedItems, item])
    } else {
      setSelectedItems(selectedItems.filter(_item => _item.id !== item.id))
    }
  }

  const isChecked = () => {
    return selectedItems.findIndex(_item => _item.id === item.id) !== -1
  }

  useEffect(() => {
    const HIGHLIGHT_TIME = 10_000
    setTimeout(() => setRecentHighlight(false), HIGHLIGHT_TIME)
  }, [])

  const workflowStatus = workflowStatusList?.find(
    status => status.id === item.workflow_status_id
  )
  const assigneeData = assigneeList?.find(
    assignee => assignee.id === item.workflow_assignee_id
  )

  return (
    <div
      className={clsx(
        s.row,
        {
          [s.grouped]: item.grouped,
        },
        recentHighlight ? s.backgroundActive : null
      )}
    >
      <div className={s.left}>
        <div role="none" onClick={event => event.stopPropagation()}>
          <Checkbox checked={isChecked()} onChange={handleSelect} />
        </div>
        <p className={s.titleWrapper}>
          <div className={s.codeTitle} role="none" onClick={handleClick}>
            <div className={s.code}>#{item.code}</div>
            <div className={s.title}>{item.title}</div>
            {item?.vertical?.key && (
              <div className={s.vertical}>
                <Tag className={s.tag}>{item?.vertical?.key}</Tag>
              </div>
            )}
          </div>
        </p>
      </div>

      <div className={s.right}>
        {toggleState?.data.workflow_enabled === 1 && (
          <StatusDropdown
            companyId={companyId}
            statusList={workflowStatusList}
            itemStatusId={item.workflow_status_id}
            itemId={item.id}
            isPrivateDoc={true}
          >
            <div className={s.itemStatus}>
              <div
                className={clsx(s.statusColor, {
                  [s.noColor]: workflowStatus?.name === 'No Status',
                })}
                style={{
                  backgroundColor: `#${workflowStatus?.color}`,
                  outline: `3px solid #${workflowStatus?.color}25`,
                }}
              />
              <div className={s.statusText}>{workflowStatus?.name}</div>
              <Icon
                icon="icon-park-solid:down-one"
                className={s.dropdownIcon}
              />
            </div>
          </StatusDropdown>
        )}

        <div className={s.avatarWrapper}>
          {toggleState?.data.workflow_enabled === 1 && (
            <AssigneeDropdown
              companyId={companyId}
              assigneeList={assigneeList}
              itemId={item.id}
              assigneeId={item.workflow_assignee_id}
              isPrivateDoc={true}
              showTooltip={true}
            >
              <div
                className={clsx(s.pvtDocAssignee, {
                  [s.avatarBorder]: !assigneeData?.full_name,
                })}
                role="none"
              >
                {!assigneeData?.full_name ? (
                  <Icon icon="lucide:user" height={16} />
                ) : !assigneeData?.avatar ? (
                  <Avatar className={s.avatar}>
                    {assigneeData?.full_name[0]}
                  </Avatar>
                ) : (
                  <Avatar src={assigneeData?.avatar} />
                )}
              </div>
            </AssigneeDropdown>
          )}
        </div>
        <div
          className={s.actionsWrapper}
          role="none"
          onClick={event => event.stopPropagation()}
        >
          <QuickActions
            item={item}
            hideWorkflow={toggleState?.data.workflow_enabled === 0}
            onWorkflowLogClick={handleWorkflowLogClick}
            onDownload={handleDownload}
            onDelete={handleDelete}
          />
        </div>
      </div>
    </div>
  )
}

export default Row
